/**
 * Auth Context with API Helper
 * Extracted from App.js - Phase 8A Context Refactor
 */
import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

// Resolve backend URL. Backend is served on the same origin as the
// frontend (backend routes prefixed with /api are proxied through the
// ingress), so window.location.origin is correct for every deployment
// target — preview, custom prod domain (e.g. app.verifieddemand.com),
// and default emergent.host prod domain alike. The env var fallback
// is only needed for SSR / build-time contexts where `window` is
// undefined.
const getBackendUrl = () => {
  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

// Admin impersonation ("Login as"): a MASTER_ADMIN opens a brand-new tab via
// /?impersonate_token=... to view a team member's exact dashboard. The token
// is stashed in sessionStorage (NOT localStorage) so it stays isolated to
// that one tab and never clobbers the admin's own logged-in session, which
// lives in localStorage and is shared across all tabs of this origin. This
// runs once at module load, before any component reads the token.
if (typeof window !== "undefined") {
  const params = new URLSearchParams(window.location.search);
  const impersonateToken = params.get("impersonate_token");
  if (impersonateToken) {
    sessionStorage.setItem("impersonation_token", impersonateToken);
    params.delete("impersonate_token");
    const newSearch = params.toString();
    const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : "") + window.location.hash;
    window.history.replaceState({}, "", newUrl);
  }
}

const getStoredToken = () => {
  if (typeof window === "undefined") return null;
  return sessionStorage.getItem("impersonation_token") || localStorage.getItem("token");
};

// Log the resolved API URL for debugging
if (typeof window !== 'undefined') {
  console.log('[AUTH] API URL resolved to:', API, '(hostname:', window.location.hostname, ')');
}

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(getStoredToken());
  const [loading, setLoading] = useState(true);
  
  // P1-E: Sub-master dealer switching and read-only mode
  const [viewingDealerId, setViewingDealerId] = useState(null);
  const [isReadOnly, setIsReadOnly] = useState(false);

  // Create stable API instance
  const apiFetch = useCallback(async (endpoint, options = {}) => {
    const currentToken = getStoredToken();
    const isFormData = typeof FormData !== "undefined" && options.data instanceof FormData;
    const config = {
      ...options,
      headers: {
        // FormData uploads must NOT set Content-Type manually — axios/the
        // browser needs to generate the multipart boundary itself.
        ...(isFormData ? {} : { "Content-Type": "application/json" }),
        ...(currentToken ? { Authorization: `Bearer ${currentToken}` } : {}),
        ...options.headers,
      },
    };
    
    // P1-E: Add viewing dealer header for sub-masters
    const viewingDealer = localStorage.getItem("viewing_dealer_id");
    if (viewingDealer) {
      config.headers["X-Viewing-Dealer"] = viewingDealer;
    }

    try {
      const response = await axios({
        url: `${API}${endpoint}`,
        ...config,
      });
      return response;
    } catch (err) {
      // CRITICAL FIX: Only logout on 401 from identity endpoints
      // Do NOT logout on 403 (permission denied) - user is authenticated but lacks permission
      // Do NOT logout on 401 from non-identity endpoints - could be stale data
      const isIdentityEndpoint = endpoint === "/me" || endpoint === "/auth/me";
      
      if (err.response?.status === 401 && isIdentityEndpoint) {
        // Session truly expired - clear and logout
        console.log("[AUTH] Session expired - logging out");
        localStorage.removeItem("token");
        localStorage.removeItem("viewing_dealer_id");
        sessionStorage.removeItem("impersonation_token");
        setToken(null);
        setUser(null);
        setViewingDealerId(null);
        setIsReadOnly(false);
      }
      // For 403 or 401 on non-identity endpoints, just let the error propagate
      // The calling component should handle displaying the error
      throw err;
    }
  }, []);

  const fetchUser = useCallback(async () => {
    const currentToken = getStoredToken();
    if (!currentToken) {
      setLoading(false);
      return;
    }
    try {
      // Fetch from /me endpoint which includes role, dealer_id, etc.
      const response = await apiFetch("/me", { method: "GET" });
      setUser(response.data);
    } catch (e) {
      // Fallback to /auth/me if /me fails
      try {
        const authResponse = await apiFetch("/auth/me", { method: "GET" });
        setUser(authResponse.data);
      } catch (e2) {
        localStorage.removeItem("token");
        sessionStorage.removeItem("impersonation_token");
        setToken(null);
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    sessionStorage.removeItem("impersonation_token");
    localStorage.setItem("token", access_token);
    setToken(access_token);
    
    // Fetch full user data including role from /me endpoint
    try {
      const meResponse = await axios.get(`${API}/me`, {
        headers: { Authorization: `Bearer ${access_token}` }
      });
      setUser(meResponse.data);
      return meResponse.data;
    } catch (e) {
      // Fallback to login response user data
      setUser(userData);
      return userData;
    }
  };

  const register = async (email, password, firstName, lastName) => {
    const response = await axios.post(`${API}/auth/register`, {
      email, password, first_name: firstName, last_name: lastName
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("viewing_dealer_id");
    sessionStorage.removeItem("impersonation_token");
    setToken(null);
    setUser(null);
    setViewingDealerId(null);
    setIsReadOnly(false);
  }, []);

  // P1-E: Switch dealer for sub-masters
  const switchDealer = useCallback(async (dealerId, readOnly = false) => {
    if (dealerId) {
      localStorage.setItem("viewing_dealer_id", dealerId);
      setViewingDealerId(dealerId);
      setIsReadOnly(readOnly);
    } else {
      localStorage.removeItem("viewing_dealer_id");
      setViewingDealerId(null);
      setIsReadOnly(false);
    }
  }, []);

  // P1-E: Check if user can modify (not read-only)
  const canModify = useCallback(() => {
    if (isReadOnly) return false;
    if (user?.role === "SUB_MASTER") return false;
    if (user?.role === "DEALER_USER") return false;
    return true;
  }, [user, isReadOnly]);

  const resetPassword = async (email) => {
    const response = await axios.post(`${API}/auth/reset-password`, { email });
    return response.data;
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      register, 
      logout, 
      resetPassword, 
      apiFetch,
      // P1-E: Sub-master support
      viewingDealerId,
      isReadOnly,
      switchDealer,
      canModify,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
